import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { FEE_FIXED, FEE_RATE } from "@/lib/haul";

export const DEFAULT_FLIP = { roi_pct: 100, min_profit: null, charge_shipping: false, ship_mode: "estimate", flat_ship: 8 };
const num = (v) => (v === null || v === undefined || v === "" || Number.isNaN(Number(v)) ? null : Number(v));

let cache = null;
const subs = new Set();
export function useFlipSettings() {
  const [s, setS] = useState(cache || DEFAULT_FLIP);
  useEffect(() => {
    subs.add(setS);
    if (!cache) api.get("/research/settings", { swr: true }).then(({ data }) => { cache = { ...DEFAULT_FLIP, ...(data.flip || {}) }; subs.forEach((f) => f(cache)); }).catch(() => {});
    return () => subs.delete(setS);
  }, []);
  const save = async (next) => {
    const { data } = await api.put("/research/flip-settings", next);
    cache = { ...DEFAULT_FLIP, ...data.flip };
    subs.forEach((f) => f(cache));
  };
  return [s, save];
}

// Shipping the seller pays: 0 when buyers are charged, else the flat rate or the item's estimate.
export function shipCost(run, s) {
  if (s.charge_shipping) return 0;
  if (s.ship_mode === "flat") return Number(s.flat_ship || 0);
  return num(run?.ship_est) ?? Number(s.flat_ship || 0);
}

// Max buy price that still hits the target (lower of ROI % and $ profit when both are set), and real ROI when a buy price is known.
export function flipMath(price, ship, target, buy) {
  const p = Number(price || 0);
  const fees = p > 0 ? p * FEE_RATE + FEE_FIXED : 0;
  const net = p - fees - ship;
  const roi = num(target?.roi_pct);
  const minP = num(target?.min_profit);
  const caps = [roi !== null ? net / (1 + roi / 100) : null, minP !== null ? net - minP : null].filter((x) => x !== null);
  const maxBuy = caps.length ? Math.max(0, Math.min(...caps)) : null;
  const b = num(buy);
  const profit = b !== null ? net - b : null;
  const realRoi = b ? (profit / b) * 100 : null;
  const hits = b === null ? null : (roi === null || (realRoi ?? Infinity) >= roi) && (minP === null || profit >= minP);
  return { price: p, fees, ship, net, maxBuy, buy: b, profit, realRoi, hits };
}

const BT = "snaplist.worthTarget";
const BR = "snaplist.worthReceipt";
const read = (k, d) => { try { return JSON.parse(localStorage.getItem(k)) ?? d; } catch { return d; } };
// The What's it worth queue's own target and receipt photos (kept with the queue on this device).
export function useWorthFlip() {
  const [target, setTargetS] = useState(() => read(BT, { use_default: true, roi_pct: "", min_profit: "" }));
  const [receipts, setReceiptsS] = useState(() => read(BR, []));
  const setTarget = (v) => { setTargetS(v); localStorage.setItem(BT, JSON.stringify(v)); };
  const setReceipts = (v) => { setReceiptsS(v); localStorage.setItem(BR, JSON.stringify(v)); };
  return { target, setTarget, receipts, setReceipts };
}
export const effectiveTarget = (t, s) => (t.use_default ? s : { roi_pct: num(t.roi_pct), min_profit: num(t.min_profit) });

export const usd = (n) => `${n < 0 ? "−" : ""}$${Math.abs(Number(n || 0)).toFixed(2)}`;
export const targetLabel = (t) => [num(t?.roi_pct) !== null && `${num(t.roi_pct)}% ROI`, num(t?.min_profit) !== null && `$${num(t.min_profit)} profit`].filter(Boolean).join(" + ") || "no target";
